/**
 * Shared design tokens + helpers for TheNewsPulse.pro modern UI.
 * Centralises the palette so every page renders on-brand.
 */
export const NP_COLORS = {
  navy:      "#0A1F3A",     // hero background
  navyDeep:  "#061426",     // hero gradient bottom
  brandBlue: "#1E6EEF",     // primary CTA + .pro highlight
  brandRed:  "#E63946",     // breaking-news / accent
  ink:       "#0F172A",     // main text
  sub:       "#475569",     // secondary text
  line:      "#E2E8F0",     // dividers
  bg:        "#F8FAFC",     // canvas
};

/**
 * Colours + Lucide-icon key for the 9 hero category tiles in the mockup.
 * Kept declarative so the tiles can be re-ordered in a single edit.
 */
export const CATEGORY_TILES = [
  { key: "world",       label: "World",       sub: "News",        icon: "Globe2",    bg: "from-blue-500 to-blue-600",     path: "world" },
  { key: "india",       label: "India",       sub: "News",        icon: "Landmark",  bg: "from-orange-500 to-orange-600", path: "india" },
  { key: "agriculture", label: "Agriculture", sub: "& Farming",   icon: "Sprout",    bg: "from-emerald-500 to-emerald-600", path: "agriculture" },
  { key: "aviation",    label: "Aviation",    sub: "& Airports",  icon: "Plane",     bg: "from-sky-500 to-sky-600",       path: "aviation" },
  { key: "travel",      label: "Travel",      sub: "& Tourism",   icon: "Luggage",   bg: "from-violet-500 to-violet-600", path: "travel" },
  { key: "health",      label: "Health",      sub: "& Science",   icon: "HeartPulse",bg: "from-rose-500 to-rose-600",     path: "health" },
  { key: "nature",      label: "Nature",      sub: "& Environment", icon: "Leaf",    bg: "from-green-600 to-green-700",   path: "nature" },
  { key: "business",    label: "Business",    sub: "& Economy",   icon: "BarChart3", bg: "from-amber-500 to-amber-600",   path: "business" },
  { key: "more",        label: "More",        sub: "Categories",  icon: "MoreHorizontal", bg: "from-slate-500 to-slate-600", path: null },
];

/**
 * Top-nav labels + their category-path counterpart. Keeps the desktop bar
 * consistent with the tile grid and the topic route.
 */
// Main nav — 14 concise sections + "All Topics" mega-menu.
export const TOP_NAV = [
  { label: "Latest",        to: "/" },
  { label: "World",         to: "/topic/world" },
  { label: "Local",         to: "/local" },
  { label: "Politics",      to: "/topic/government" },
  { label: "Business",      to: "/topic/business" },
  { label: "Technology",    to: "/topic/technology" },
  { label: "Health",        to: "/topic/health" },
  { label: "Science",       to: "/topic/science" },
  { label: "Climate",       to: "/topic/nature" },
  { label: "Sports",        to: "/topic/sports" },
  { label: "Entertainment", to: "/topic/entertainment" },
  { label: "Travel",        to: "/topic/travel" },
  { label: "Lifestyle",     to: "/topic/lifestyle" },
  { label: "Opinion",       to: "/opinion" },
];

// Sections surfaced in the "All Topics" mega-menu, grouped for the
// dropdown. Each key is a group heading, each item is [label, path].
export const ALL_TOPICS = [
  {
    group: "News",
    items: [
      ["Breaking News", "/topic/breaking_news"],
      ["World", "/topic/world"],
      ["Local & Regional", "/local"],
      ["Investigations", "/topic/investigations"],
      ["Solutions & Good News", "/topic/solutions"],
      ["Human Interest", "/topic/human_interest"],
    ],
  },
  {
    group: "Politics & Society",
    items: [
      ["Politics & Government", "/topic/government"],
      ["Law & Courts", "/topic/law"],
      ["Crime & Public Safety", "/topic/public_safety"],
      ["Defense & Military", "/topic/defense"],
      ["Society & Community", "/topic/society"],
      ["Culture & Religion", "/topic/culture"],
    ],
  },
  {
    group: "Business & Economy",
    items: [
      ["Business", "/topic/business"],
      ["Markets & Finance", "/topic/markets"],
      ["Real Estate", "/topic/real_estate"],
      ["Jobs & Careers", "/topic/jobs"],
      ["Consumer News", "/topic/consumer"],
      ["Media", "/topic/media"],
    ],
  },
  {
    group: "Science & Tech",
    items: [
      ["Technology", "/topic/technology"],
      ["Artificial Intelligence", "/topic/artificial_intelligence"],
      ["Science & Research", "/topic/science"],
      ["Health & Medicine", "/topic/health"],
      ["Energy", "/topic/energy"],
    ],
  },
  {
    group: "Planet & Life",
    items: [
      ["Climate & Environment", "/topic/nature"],
      ["Weather & Disasters", "/topic/weather"],
      ["Agriculture", "/farmerspulse"],
      ["Food & Drink", "/topic/food"],
      ["Education", "/topic/education"],
      ["Lifestyle", "/topic/lifestyle"],
    ],
  },
  {
    group: "Sports · Travel · Culture",
    items: [
      ["Sports", "/topic/sports"],
      ["Entertainment", "/topic/entertainment"],
      ["Travel & Tourism", "/topic/travel"],
      ["Aviation", "/aviation"],
      ["Transportation", "/topic/transportation"],
      ["Infrastructure", "/topic/infrastructure"],
      ["Events", "/topic/events"],
    ],
  },
  {
    group: "Voices",
    items: [
      ["Opinion & Analysis", "/opinion"],
      ["Editorials", "/opinion/editorials"],
      ["Investigations", "/topic/investigations"],
    ],
  },
];

/**
 * 7 landmark regions rendered as image cards on the home page. Images are
 * lightweight Unsplash CDN URLs so we avoid bundling.
 */
export const REGIONS = [
  { key: "asia",         label: "Asia",         img: "https://images.unsplash.com/photo-1542931287-023b922fa89b?w=400&h=280&fit=crop", path: "AS" },
  { key: "north-america",label: "North America",img: "https://images.unsplash.com/photo-1548019979-4265fb2b8a09?w=400&h=280&fit=crop", path: "NA" },
  { key: "europe",       label: "Europe",       img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=400&h=280&fit=crop", path: "EU" },
  { key: "middle-east",  label: "Middle East",  img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400&h=280&fit=crop", path: "ME" },
  { key: "africa",       label: "Africa",       img: "https://images.unsplash.com/photo-1523805009345-7448845a9e53?w=400&h=280&fit=crop", path: "AF" },
  { key: "latin-america",label: "Latin America",img: "https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=400&h=280&fit=crop", path: "SA" },
  { key: "oceania",      label: "Oceania",      img: "https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=400&h=280&fit=crop", path: "OC" },
];

/**
 * Sister-network chips shown on the footer network band. Order == order in
 * the mockup.
 */
export const NETWORK_SITES = [
  { key: "socialpulse",     label: "SocialPulse",     icon: "MessageCircle", href: "https://socialpulse.app",       color: "text-emerald-600" },
  { key: "indias",          label: "Indias.com",      icon: "Landmark",      href: "https://indias.com",            color: "text-rose-500" },
  { key: "farmerspulse",    label: "FarmersPulse",    icon: "Sprout",        href: "https://farmerspulse.net",      color: "text-green-600" },
  { key: "airlinesairports",label: "AirlinesAirports",icon: "Plane",         href: "https://airlinesairports.com",  color: "text-blue-600" },
  { key: "travelerpulse",   label: "TravelerPulse",   icon: "Luggage",       href: "https://travelerpulse.pro",     color: "text-cyan-600" },
  { key: "naturepulse",     label: "NaturePulse",     icon: "Leaf",          href: "https://naturepulse.net",       color: "text-emerald-700" },
];

/**
 * Pretty format like "2 hours ago" / "5m ago". Locale-agnostic.
 */
export function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hour${Math.floor(s / 3600) > 1 ? "s" : ""} ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

/**
 * Extract the primary human-friendly category slug from a category path
 * like `aviation/aircraft/a380` → "aviation".
 */
export function primaryCategoryPath(cats) {
  const first = (cats || [])[0] || "";
  return first.split("/")[0] || "";
}

export function categoryLabel(cats) {
  const first = (cats || [])[0] || "";
  const last = first.split("/").pop() || "";
  return last.replace(/_/g, " ").toUpperCase();
}
